import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FitMode } from '../../../models/led-image.util';

type Tool = 'pencil' | 'eraser' | 'eyedropper' | 'fill' | 'row' | 'col' | 'line' | 'rect' | 'circle';

@Component({
  selector: 'app-tool-dock',
  templateUrl: './tool-dock.component.html',
  styleUrls: ['./tool-dock.component.scss'],
  standalone: false,
})
export class ToolDockComponent {
  @Input() tool: Tool = 'pencil';
  @Input() color = '#00ccff';
  @Input() recentColors: string[] = [];
  @Input() brightness = 15;
  @Input() sending = false;
  @Input() playing = false;
  @Input() loop = true;
  @Input() multiFrame = false;
  @Input() hasImage = false;
  @Input() fitMode: FitMode = 'cover';
  @Input() imgBrightness = 0;
  @Input() imgContrast = 0;
  @Input() imgSaturation = 0;
  @Input() cropX = 0;
  @Input() cropY = 0;

  @Output() toolChange = new EventEmitter<Tool>();
  @Output() pick = new EventEmitter<string>();
  @Output() undo = new EventEmitter<void>();
  @Output() redo = new EventEmitter<void>();
  @Output() clear = new EventEmitter<void>();
  @Output() brightnessChange = new EventEmitter<number>();
  @Output() send = new EventEmitter<void>();
  @Output() togglePreview = new EventEmitter<void>();
  @Output() loopChange = new EventEmitter<boolean>();
  @Output() importPhoto = new EventEmitter<void>();
  @Output() fitModeChange = new EventEmitter<FitMode>();
  @Output() imgBrightnessChange = new EventEmitter<number>();
  @Output() imgContrastChange = new EventEmitter<number>();
  @Output() imgSaturationChange = new EventEmitter<number>();
  @Output() cropXChange = new EventEmitter<number>();
  @Output() cropYChange = new EventEmitter<number>();
  @Output() resetImport = new EventEmitter<void>();
  @Input() pasteEnabled = false;
  @Output() copyFrame = new EventEmitter<void>();
  @Output() pasteFrame = new EventEmitter<void>();
  @Output() nudge = new EventEmitter<{ dx: number; dy: number }>();
  @Input() selectMode = false;
  @Output() toggleSelect = new EventEmitter<void>();
  @Output() deselect = new EventEmitter<void>();

  /** In a live session the board already has everything: no Send, no undo. */
  @Input() sessionMode = false;
  @Input() countdown = '';
  @Input() peopleCount = 0;
  @Output() leaveSession = new EventEmitter<void>();

  get peopleText(): string {
    const n = this.peopleCount;
    if (n <= 1) { return "You're the only one drawing"; }
    return `${n} people drawing`;
  }

  // The everyday tools sit in the dock itself; everything else lives one tap
  // away in the More sheet, so a first-timer sees three choices, not nine.
  readonly primaryTools: { id: Tool; icon: string; label: string }[] = [
    { id: 'pencil', icon: '✏️', label: 'Pencil' },
    { id: 'eraser', icon: '🧽', label: 'Erase' },
    { id: 'fill', icon: '🪣', label: 'Fill' },
  ];

  readonly extraTools: { id: Tool; icon: string; label: string }[] = [
    { id: 'line', icon: '╱', label: 'Line' },
    { id: 'rect', icon: '▭', label: 'Box' },
    { id: 'circle', icon: '○', label: 'Circle' },
    { id: 'eyedropper', icon: '💧', label: 'Pick colour' },
    { id: 'row', icon: '↔️', label: 'Fill row' },
    { id: 'col', icon: '↕️', label: 'Fill column' },
  ];

  /** Every tool, primary first — kept for callers that want the full list. */
  get tools(): { id: Tool; icon: string; label: string }[] {
    return [...this.primaryTools, ...this.extraTools];
  }

  // A fixed palette beats "recent colours" for a first-timer: the choices never
  // move, and black doubles as an eraser for people who miss the Erase tool.
  readonly palette: string[] = [
    '#ffffff', '#ff3b30', '#ff9500', '#ffd60a', '#34c759',
    '#30d5c8', '#00ccff', '#0a84ff', '#bf5af2', '#ff4d6d', '#000000',
  ];

  readonly fitModes: FitMode[] = ['cover', 'fit', 'stretch'];

  /** Plain words for what each fit does to a 40x32 board. */
  readonly fitLabels: Record<string, string> = {
    cover: 'Fill board',
    fit: 'Whole photo',
    stretch: 'Stretch',
  };
  fitLabel(m: FitMode): string { return this.fitLabels[m] || m; }

  moreOpen = false;
  toggleMore() { this.moreOpen = !this.moreOpen; }
  closeMore() { this.moreOpen = false; }

  /** True when the active tool is one of the More-sheet tools, so More reads as selected. */
  get usingExtraTool(): boolean { return this.extraTools.some(t => t.id === this.tool); }

  /** Label for the active More tool, shown under the More button. */
  get moreLabel(): string {
    const t = this.extraTools.find(x => x.id === this.tool);
    return t ? t.label : 'More';
  }

  selectTool(t: Tool) { this.toolChange.emit(t); }

  /** Pick a tool from the More sheet and get back to the canvas. */
  selectExtraTool(t: Tool) { this.toolChange.emit(t); this.closeMore(); }

  onBrightness(v: string) { this.brightnessChange.emit(+v); }
}
